import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Box, CircleDollarSign, ShieldCheck, Eye, GraduationCap, Sparkles, Send, CheckCircle2, Layers, BadgeDollarSign, ChevronDown, Flame, Calculator, Loader2 } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import ProofStats from "@/components/ProofStats";
import DemoReel from "@/components/DemoReel";
import CreatorSupportChat from "@/components/creator/CreatorSupportChat";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const TOOL_OPTIONS = ["TRELLIS", "Blender", "Midjourney", "Stable Diffusion", "Mixamo", "Substance", "ZBrush", "Other AI tools"];

const FAQ = [
  {
    q: "Is this a pay-to-work scheme?",
    a: "No — and we're upfront about the difference. Joining is free and instant. A one-time US$20 activation buys the Local Forge Kit: TRELLIS image-to-3D running on YOUR machine, rig-prep standards, export specs and lifetime pipeline access. You keep the kit forever, even if you never submit a pack. And our earn-back guarantee means your first approved pack pays more than the fee back as bounty.",
  },
  {
    q: "How exactly do I make money?",
    a: "Two ways. Bounty: every pack that passes studio QA pays you an AUD bounty on the spot, claimable immediately. Royalty: your published packs sell on the Specpixel marketplace and every sale pays you a royalty percentage, automatically, forever. One pack can keep paying you for years.",
  },
  {
    q: "When do I get my activation fee back?",
    a: "With your first approved pack. Our earn-back guarantee sets your first bounty at a minimum of AUD 30 — more than the US$20 activation — so pass QA once and you're in profit. After that, every bounty and royalty is yours.",
  },
  {
    q: "Do I need to be a professional 3D artist?",
    a: "No. The Creator Academy trains you in our exact workflow: prompt craft, TRELLIS settings, QA standards, pack assembly. If you can write a clear prompt and judge quality, you can do this. Blender basics help for rigged packs but aren't required to start.",
  },
  {
    q: "What if my pack gets rejected?",
    a: "You get detailed notes from the reviewer and resubmit as a new draft. Rejection isn't removal — your account, your kit and your academy progress stay. Repeat low-quality submissions are the only thing that ends contracts.",
  },
  {
    q: "Who is Specpixel Entertainment?",
    a: "An Australian game studio (Melbourne, ABN-registered) behind New Citizen on Steam, 24 products on the Fab asset store, and client work for studios worldwide. Real company, real storefronts, real payouts — scroll down and check the numbers yourself.",
  },
];

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} className="border border-white/10 bg-black/40">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-white/5 transition-colors">
        <span className="font-sans font-semibold text-sm text-zinc-100">{q}</span>
        <ChevronDown className={`w-4 h-4 text-nc-blue shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="font-mono text-xs text-zinc-400 leading-relaxed px-6 pb-5">{a}</p>}
    </div>
  );
}

function TryForge() {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/creators/try/recent").then((r) => setRecent(r.data.items || [])).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/creators/try", { prompt });
      setResult(r.data.id);
      setRecent((items) => [{ id: r.data.id, prompt }, ...items].slice(0, 6));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="join-creators-try" className="border border-nc-blue/40 bg-nc-blue/5 p-8 mb-16">
      <p className="font-mono text-[10px] tracking-[0.35em] text-nc-blue uppercase mb-2">Proof // don't take our word for it</p>
      <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mb-4">Try the Forge right now — free</h2>
      <p className="font-mono text-xs text-zinc-400 leading-relaxed max-w-2xl mb-6">
        Type a game asset idea below and watch the exact pipeline you'd use as a creator produce a real concept.
        One free generation, no account, no card. This is step one of the workflow you'd be paid for.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col gap-3">
          <textarea
            data-testid="try-prompt-input"
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={300}
            placeholder="e.g. Tactical police radio with worn rubber casing, game-ready low-poly style, single object on plain dark background"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue resize-none"
          />
          <button
            data-testid="try-generate-button"
            disabled={busy || prompt.trim().length < 10}
            onClick={generate}
            className="font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-5 py-3 transition-colors flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Generating (about 10s)…" : "Generate My Concept — Free"}
          </button>
          <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em]">2 free tries per day · creators get 20/day + TRELLIS 3D</p>
        </div>
        <div className="border border-white/10 bg-black/40 min-h-48 flex items-center justify-center">
          {result ? (
            <div className="w-full">
              <img data-testid="try-result-image" src={`${process.env.REACT_APP_BACKEND_URL}/api/creators/try/${result}/image`} alt="Your generated concept" className="w-full object-cover" />
              <p className="font-mono text-[10px] text-nc-green px-4 py-3 leading-relaxed">
                That's step one. As a creator you'd send this to TRELLIS for 3D, pack it, and sell it.
                <a href="#apply" className="text-nc-blue hover:text-zinc-100 underline underline-offset-2 ml-1">Apply and get the full pipeline</a>
              </p>
            </div>
          ) : (
            <p className="font-mono text-[10px] text-zinc-600 px-6 text-center">{busy ? "The Forge is drawing your concept…" : "Your concept appears here"}</p>
          )}
        </div>
      </div>
      {recent.length > 0 && (
        <div className="mt-8">
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-3">Fresh off the Forge // made by visitors, just now</p>
          <div data-testid="try-wall" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {recent.map((it, i) => (
              <div key={it.id} data-testid={`try-wall-item-${i}`} className="border border-white/10 bg-[#050505] group">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/creators/try/${it.id}/image`} alt={it.prompt} className="w-full aspect-square object-cover group-hover:opacity-90 transition-opacity" loading="lazy" />
                <p className="font-mono text-[8px] text-zinc-500 p-2 leading-relaxed line-clamp-2">{it.prompt}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function JoinCreatorsPage() {
  const [params] = useSearchParams();
  const recId = (params.get("rec") || "").trim().toUpperCase();
  const [me, setMe] = useState(undefined);
  const [leaders, setLeaders] = useState([]);
  const [prog, setProg] = useState(null);
  const [form, setForm] = useState({ portfolio_url: "", tools_used: [], experience: "", sample_prompt: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // earnings calculator
  const [calcPacks, setCalcPacks] = useState(3);
  const [calcSales, setCalcSales] = useState(6);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTool = (t) => set("tools_used", form.tools_used.includes(t) ? form.tools_used.filter((x) => x !== t) : [...form.tools_used, t]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
    api.get("/creators/leaderboard").then((r) => setLeaders(r.data.entries || [])).catch(() => {});
    api.get("/creators/program/stats").then((r) => setProg(r.data)).catch(() => {});
  }, []);

  const feeUsd = prog?.fee_usd ?? 20;
  const guaranteeAud = prog?.guarantee_bounty_aud ?? 30;
  const bountyAvg = guaranteeAud; // earn-back guarantee makes this the honest floor for pack one
  const priceAvg = 49;
  const royaltyPct = 10;
  const calcMonthlyBounty = calcPacks * bountyAvg;
  const calcMonthlyRoyalty = calcPacks * calcSales * priceAvg * (royaltyPct / 100);
  const calcMonthly = calcMonthlyBounty + calcMonthlyRoyalty;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/creators/apply", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="join-creators-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="AI 3D Creator Program — Make Game Assets with AI, Get Paid"
        description="Join the Specpixel Creator Forge: turn text prompts into game-ready 3D asset packs with TRELLIS image-to-3D. US$20 one-time activation, earn-back guarantee, AUD bounty per approved pack plus royalties on every sale. Free try on the page — no account needed."
        path="/join-creators"
        image="/assets/og-creators.png"
        keywords="AI 3D creator program, make money with AI 3D, TRELLIS image to 3D, sell game assets online, game asset marketplace, AI 3D model generator, 3D asset packs for sale, get paid to make game assets, AI side hustle 2026, game ready 3D models"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              "name": "Specpixel Creator Forge — AI 3D Pack Program",
              "description": "AI-powered 3D game asset creation program. Local TRELLIS toolkit, training academy, bounties per approved pack and royalties on marketplace sales.",
              "brand": { "@type": "Brand", "name": "Specpixel Entertainment" },
              "offers": {
                "@type": "Offer",
                "price": "20.00",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "url": "https://specpixel.com/join-creators",
              },
            },
            {
              "@type": "FAQPage",
              "mainEntity": FAQ.map((f) => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://specpixel.com/" },
                { "@type": "ListItem", "position": 2, "name": "Creator Program", "item": "https://specpixel.com/join-creators" },
              ],
            },
          ],
        }}
      />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden min-h-[85vh] flex items-center">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <img
          src="https://images.pexels.com/photos/28494632/pexels-photo-28494632.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.13] mix-blend-screen pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute top-1/4 left-1/3 w-[600px] h-[400px] bg-nc-blue/10 blur-[140px] rounded-full pointer-events-none animate-pulse-slow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28 w-full">
          <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}>
            <motion.p variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.5 }} className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-4">Initializing Creator Protocol // AI Creator Program</motion.p>
            <motion.h1 variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} data-testid="join-creators-title" className="font-sans font-bold uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl text-zinc-50 max-w-4xl">
              Make game-ready 3D packs with AI. <span className="text-nc-blue">Get paid for every one.</span>
            </motion.h1>
            <motion.p variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.6 }} className="font-mono text-sm text-zinc-400 mt-6 max-w-2xl leading-relaxed">
              Specpixel Entertainment — the Australian studio behind New Citizen on Steam and 24 Fab store products —
              is recruiting AI creators. Prompt → concept → image-to-3D → pack → published on our marketplace.
              You earn an <b className="text-zinc-200">AUD bounty the moment a pack is approved</b>, plus a
              <b className="text-zinc-200"> royalty on every sale, forever</b>.
            </motion.p>
            <motion.div variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.6 }} className="mt-8 flex flex-wrap gap-3 items-center">
              <motion.a whileHover={{ scale: 1.03, boxShadow: "0 0 25px rgba(46,107,255,0.5)" }} whileTap={{ scale: 0.97 }} href="#apply" data-testid="join-creators-apply-cta" className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3.5 transition-colors duration-300 inline-flex items-center gap-2">
                Apply Free — 2 Minutes <ArrowRight className="w-4 h-4" />
              </motion.a>
              <Link to="/marketplace" className="font-mono text-xs tracking-[0.25em] uppercase border border-white/20 text-zinc-200 px-6 py-3.5 hover:border-nc-blue hover:text-nc-blue transition-colors duration-300">
                See What Sells
              </Link>
              {prog && prog.founding_remaining > 0 && (
                <span data-testid="join-creators-founding-tag" className="font-mono text-[10px] tracking-[0.15em] uppercase text-nc-orange border border-nc-orange/40 bg-nc-orange/10 px-3 py-1.5 inline-flex items-center gap-1.5">
                  <Flame className="w-3.5 h-3.5" /> {prog.founding_remaining} of {prog.founding_total} founding spots left
                </span>
              )}
            </motion.div>
          </motion.div>
        </div>
      </header>

      <ProofStats compact testid="join-creators-proof-stats" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 w-full">

        <TryForge />

        <DemoReel />

        {/* Earn-back guarantee — the risk reversal */}
        {prog?.guarantee !== false && (
          <motion.div data-testid="join-creators-guarantee" className="border border-nc-green/40 bg-nc-green/5 p-8 mb-16 flex flex-col md:flex-row gap-6 items-start" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <BadgeDollarSign className="w-8 h-8 text-nc-green shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-sans font-bold uppercase text-lg text-nc-green">The earn-back guarantee</p>
              <p className="font-mono text-xs text-zinc-300 mt-3 leading-relaxed max-w-3xl">
                Worst case is free training and a toolkit you keep forever. Your <b className="text-zinc-100">first approved pack pays a bounty of at least
                AUD {guaranteeAud}</b> — more than your entire US${feeUsd} activation fee, back in your wallet, claimable immediately. Pass QA once and everything after is pure profit:
                every bounty, every royalty, every sale.
              </p>
            </div>
          </motion.div>
        )}

        {/* Founding creator urgency */}
        {prog && prog.founding_remaining > 0 && (
          <div data-testid="join-creators-founding" className="border border-nc-orange/40 bg-nc-orange/5 p-8 mb-16">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <p className="font-sans font-bold uppercase text-sm text-nc-orange flex items-center gap-2"><Flame className="w-4 h-4" /> Founding Creator intake</p>
              <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.15em]">{prog.active_creators} / {prog.founding_total} claimed</p>
            </div>
            <div className="h-2 bg-white/10 mb-4">
              <motion.div data-testid="founding-progress" className="h-full bg-nc-orange" initial={{ width: 0 }} whileInView={{ width: `${Math.min(100, (prog.active_creators / prog.founding_total) * 100)}%` }} viewport={{ once: true }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} />
            </div>
            <p className="font-mono text-[11px] text-zinc-400 leading-relaxed max-w-3xl">
              The first {prog.founding_total} activated creators carry <b className="text-zinc-200">Founding Creator</b> status permanently:
              priority QA review, featured marketplace placement, and a permanent badge on your creator profile.
              When the spots are gone, the status is gone.
            </p>
          </div>
        )}

        <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mb-8">What you get — and what you earn</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-16">
          {[
            { icon: Sparkles, t: "Full AI Pipeline", d: "Text-to-image concept generation plus TRELLIS.2 image-to-3D built into your dashboard. 20 concepts + 10 3D generations per day, on us." },
            { icon: CircleDollarSign, t: "Bounty Per Pack", d: "Every pack that passes studio QA pays you an AUD bounty on the spot — set at review, claimable immediately." },
            { icon: Layers, t: "Royalties Forever", d: "Your published packs sell on the Specpixel marketplace. Every sale pays you an ongoing royalty percentage, automatically." },
            { icon: GraduationCap, t: "Real Training", d: "The Creator Academy teaches our exact workflow: prompt craft, TRELLIS settings, rigging prep, pack standards. Finish it to unlock submissions." },
          ].map((c, i) => (
            <motion.div key={c.t} data-testid={`join-creators-perk-${i}`} className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}>
              <c.icon className="w-5 h-5 text-nc-blue mb-3" strokeWidth={1.75} />
              <p className="font-sans font-bold uppercase text-sm text-zinc-100">{c.t}</p>
              <p className="font-mono text-[11px] text-zinc-500 mt-2 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>

        {/* Earnings calculator */}
        <div data-testid="join-creators-calculator" className="border border-white/10 bg-black/40 p-8 mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Calculator className="w-5 h-5 text-nc-blue" strokeWidth={1.75} />
            <h2 className="font-sans font-bold uppercase tracking-tight text-xl sm:text-2xl text-zinc-50">Run your own numbers</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
            <div className="flex flex-col gap-6">
              <div>
                <div className="flex justify-between mb-2">
                  <label className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Packs you submit per month</label>
                  <span data-testid="calc-packs-value" className="font-mono text-xs text-nc-blue">{calcPacks}</span>
                </div>
                <input data-testid="calc-packs-slider" type="range" min="1" max="8" value={calcPacks} onChange={(e) => setCalcPacks(Number(e.target.value))} className="w-full accent-nc-blue" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <label className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Sales per published pack / month</label>
                  <span data-testid="calc-sales-value" className="font-mono text-xs text-nc-blue">{calcSales}</span>
                </div>
                <input data-testid="calc-sales-slider" type="range" min="0" max="20" value={calcSales} onChange={(e) => setCalcSales(Number(e.target.value))} className="w-full accent-nc-blue" />
              </div>
              <p className="font-mono text-[9px] text-zinc-600 leading-relaxed uppercase tracking-[0.1em]">
                Illustrative model — first-pack bounty floor AUD {guaranteeAud} (earn-back guarantee) · avg sale AUD {priceAvg} · {royaltyPct}% royalty. Bounties are set at review and sales vary by pack quality.
              </p>
            </div>
            <div className="border border-nc-blue/30 bg-nc-blue/5 p-6 flex flex-col justify-center gap-4">
              <div>
                <p className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase">Month one (bounties)</p>
                <p data-testid="calc-bounty-total" className="font-sans font-bold text-2xl text-zinc-50">AUD {calcMonthlyBounty.toLocaleString("en-AU")}</p>
              </div>
              <div>
                <p className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase">+ Royalties every month after</p>
                <p data-testid="calc-royalty-total" className="font-sans font-bold text-2xl text-nc-green">AUD {calcMonthlyRoyalty.toLocaleString("en-AU", { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="border-t border-white/10 pt-3">
                <p className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase">Year one potential</p>
                <p data-testid="calc-year-total" className="font-sans font-bold text-3xl text-nc-blue">AUD {(calcMonthlyBounty * 12 + calcMonthlyRoyalty * 12).toLocaleString("en-AU", { maximumFractionDigits: 0 })}</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mb-8">The workflow you'll master</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-16">
          {[
            { n: "01", t: "Concept", d: "Prompt the Forge for a clean asset concept — one object, neutral background, orthographic view." },
            { n: "02", t: "Generate", d: "Send the concept to TRELLIS image-to-3D. Inspect the mesh in the built-in 3D viewer." },
            { n: "03", t: "Assemble", d: "Group approved models into a themed pack — cover render, metadata, category." },
            { n: "04", t: "Earn", d: "Studio QA approves → bounty paid. Pack goes live → royalties on every sale, forever." },
          ].map((s, i) => (
            <motion.div key={s.n} data-testid={`join-creators-step-${s.n}`} className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}>
              <p className="font-sans font-bold text-3xl text-nc-blue/60">{s.n}</p>
              <p className="font-sans font-bold uppercase text-sm text-zinc-100 mt-3">{s.t}</p>
              <p className="font-mono text-[11px] text-zinc-500 mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="border border-nc-blue/30 bg-nc-blue/5 p-8 mb-16 flex flex-col md:flex-row gap-6">
          <CircleDollarSign className="w-6 h-6 text-nc-blue shrink-0" strokeWidth={1.75} />
          <div>
            <p className="font-sans font-bold uppercase text-sm text-zinc-100">One-time activation — US${feeUsd}, less than a takeaway meal</p>
            <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed max-w-3xl">
              Applying is free and approval is instant. A one-time activation (US${feeUsd}, secure Stripe checkout — cards &amp; crypto) buys your
              <b className="text-zinc-200"> Local Forge Kit</b>: TRELLIS image-to-3D running on <b className="text-zinc-200">your own machine</b> —
              unlimited generations with zero studio credit limits — plus rig-prep standards, export specs and lifetime
              pipeline access. No subscriptions, no monthly fees — most tools charge more than this every single month.
              And your first approved pack pays it straight back with change.
            </p>
          </div>
        </div>

        {leaders.length > 0 && (
          <div data-testid="join-creators-leaderboard" className="mb-16">
            <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mb-8">Creators already earning</h2>
            <div className="border border-white/10">
              {leaders.map((e) => (
                <div key={e.rank} data-testid={`creator-leader-${e.rank}`} className="grid grid-cols-[50px_1fr_auto] items-center border-b border-white/5 px-5 py-3.5 bg-black/40">
                  <span className={`font-sans font-bold text-lg ${e.rank === 1 ? "text-nc-orange" : "text-zinc-600"}`}>#{e.rank}</span>
                  <div>
                    <p className="font-sans font-semibold text-sm text-zinc-100">{e.name}</p>
                    <p className="font-mono text-[9px] text-zinc-500">{e.published} published pack{e.published === 1 ? "" : "s"}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-xs text-nc-green">AUD {e.earned}</p>
                    <p className="font-mono text-[8px] text-zinc-600">earned (bounty + royalty)</p>
                  </div>
                </div>
              ))}
            </div>
            {prog && (
              <p className="font-mono text-[10px] text-zinc-500 mt-3 uppercase tracking-[0.15em]">
                Live program records // {prog.active_creators} activated · {prog.packs_published} packs published · AUD {prog.paid_to_creators_aud.toLocaleString("en-AU")} paid to creators
              </p>
            )}
          </div>
        )}

        <div className="border border-white/10 bg-black/40 p-8 mb-16 flex flex-col md:flex-row gap-6">
          <Eye className="w-6 h-6 text-nc-blue shrink-0" strokeWidth={1.75} />
          <div>
            <p className="font-sans font-bold uppercase text-sm text-zinc-100">Original work only — IP is everything</p>
            <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed max-w-3xl">
              No copyrighted characters, brands or "in the style of" prompts — ever. Packs are reviewed by hand and every
              generation is logged against your account. This protects the marketplace, the studio, and your royalties.
            </p>
          </div>
        </div>

        {/* Objection-killing FAQ */}
        <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mb-8">Straight answers</h2>
        <div className="flex flex-col gap-2 mb-16 max-w-4xl">
          {FAQ.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} testid={`join-creators-faq-${i}`} />)}
        </div>

        {/* Crawlable explainer — search engines read this, humans skim it */}
        <article data-testid="join-creators-explainer" className="max-w-4xl mb-16">
          <h2 className="font-sans font-bold uppercase tracking-tight text-xl sm:text-2xl text-zinc-50 mb-5">What is the Specpixel Creator Forge?</h2>
          <div className="font-mono text-xs text-zinc-400 leading-relaxed flex flex-col gap-4">
            <p>
              The Creator Forge is an AI 3D creator program run by Specpixel Entertainment, an Australian game studio behind New Citizen on Steam
              and 24 products on the Fab asset store. It turns text prompts into game-ready 3D models using TRELLIS image-to-3D technology —
              the same class of AI 3D model generators reshaping how game assets are made — and wraps it in a complete commercial pipeline:
              concept generation, 3D conversion, pack assembly, quality review and a live marketplace with real buyers.
            </p>
            <p>
              If you've searched for ways to make money with AI 3D, sell game assets online, or get paid for prompt-driven 3D work, this is the
              operational version of that idea: you build themed asset packs — rigged clothing, modular environment kits, props — submit them for
              studio QA, and earn an AUD bounty for every approved pack plus an ongoing royalty every time a pack sells on the Specpixel marketplace.
              One pack can keep paying for years.
            </p>
            <p>
              The one-time US$20 activation buys the Local Forge Kit outright — local TRELLIS setup for unlimited generations on your own GPU,
              a prompt vault of proven patterns, ready-to-build pack blueprints, a QA pre-check that predicts rejection, studio reference models and
              a completion certificate. There are no subscriptions and no revenue share on the toolkit. Your first approved pack pays a guaranteed
              bounty that more than covers the activation — that's the earn-back guarantee, written into how the program pays out.
            </p>
          </div>
        </article>

        <div id="apply" className="max-w-3xl mx-auto">
          {me === undefined ? (
            <div className="border border-white/10 p-10 text-center font-mono text-xs text-zinc-600">Checking your account…</div>
          ) : me === null ? (
            <div data-testid="join-creators-auth-gate" className="border border-nc-blue/40 bg-nc-blue/5 p-10 text-center flex flex-col items-center gap-5">
              <ShieldCheck className="w-10 h-10 text-nc-blue" strokeWidth={1.5} />
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">Sign in to apply</h2>
              <p className="font-mono text-xs text-zinc-400 leading-relaxed max-w-md">
                Applications are tied to your Specpixel account. Create a free account (or sign in), then come straight back here — the form takes two minutes.
              </p>
              <Link to={recId ? `/auth?rec=${recId}` : "/auth"} data-testid="join-creators-signin-cta" className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3.5 hover:bg-nc-blue/80 transition-colors duration-300 inline-flex items-center gap-2">
                Sign In / Create Account <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : me.role === "creator" ? (
            <div data-testid="join-creators-already" className="border border-nc-green/40 bg-nc-green/5 p-10 text-center">
              <CheckCircle2 className="w-10 h-10 text-nc-green mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">You're already a creator</h2>
              <Link to="/creators" className="inline-block mt-4 font-mono text-xs tracking-[0.25em] uppercase text-nc-blue border border-nc-blue/40 px-6 py-3 hover:bg-nc-blue/10 transition-colors">Open the Creator Forge</Link>
            </div>
          ) : me.role !== "citizen" ? (
            <div data-testid="join-creators-role-conflict" className="border border-nc-orange/40 bg-nc-orange/5 p-10 text-center">
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">Account already has a role</h2>
              <p className="font-mono text-xs text-zinc-400 mt-3 max-w-md mx-auto">Your account is registered as <span className="text-nc-orange">{me.role}</span>. Use a separate account for the creator program, or contact the studio.</p>
            </div>
          ) : done ? (
            <div data-testid="creator-application-success" className="border border-nc-green/40 bg-nc-green/5 p-10 text-center">
              <CheckCircle2 className="w-10 h-10 text-nc-green mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">You're in — instantly approved</h2>
              <p className="font-mono text-xs text-zinc-400 mt-4 leading-relaxed max-w-md mx-auto">
                No review queue, no waiting. Your creator account is live right now — one step left: activate your
                Local Forge Kit and start building. Your first approved pack pays the fee back with change.
              </p>
              <Link to="/creators" data-testid="creator-success-activate-cta" className="inline-block mt-6 font-mono text-xs tracking-[0.25em] uppercase bg-nc-orange text-black px-6 py-3.5 hover:bg-nc-orange/80 transition-colors duration-300">
                Activate Your Forge — US$20
              </Link>
            </div>
          ) : (
            <form data-testid="creator-application-form" onSubmit={submit} className="border border-white/10 bg-black/40 p-8 flex flex-col gap-5">
              <div className="flex items-center gap-3 mb-2">
                <Box className="w-5 h-5 text-nc-blue" strokeWidth={1.75} />
                <h2 className="font-sans font-bold uppercase text-xl text-zinc-50">Apply to the Creator Forge</h2>
              </div>
              <div data-testid="creator-apply-account" className="border border-white/10 bg-black/40 px-4 py-3 flex items-center gap-3">
                <ShieldCheck className="w-4 h-4 text-nc-green shrink-0" />
                <p className="font-mono text-[11px] text-zinc-300">Applying as <span className="text-zinc-100 font-bold">{me.name || me.email}</span> <span className="text-zinc-600">// {me.email}</span></p>
              </div>
              <div>
                <label className={labelCls}>Portfolio URL (ArtStation, Sketchfab…)</label>
                <input data-testid="creator-apply-portfolio" className={inputCls} value={form.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} placeholder="https://artstation.com/you" maxLength={300} />
              </div>
              <div>
                <label className={labelCls}>Tools you've used</label>
                <div className="flex flex-wrap gap-2">
                  {TOOL_OPTIONS.map((t) => (
                    <button key={t} type="button" data-testid={`creator-apply-tool-${t.replace(/\s/g, "-")}`} onClick={() => toggleTool(t)} className={`font-mono text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border transition-colors ${form.tools_used.includes(t) ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Your experience with AI 3D workflows *</label>
                <textarea data-testid="creator-apply-experience" required minLength={20} rows={3} className={`${inputCls} resize-none`} value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. Built modular kits with Meshy/Blender for two indie teams, comfortable with retopo and rig prep…" />
              </div>
              <div>
                <label className={labelCls}>Sample prompt — sell us your prompt craft *</label>
                <textarea data-testid="creator-apply-prompt" required minLength={10} rows={3} className={`${inputCls} resize-none`} value={form.sample_prompt} onChange={(e) => set("sample_prompt", e.target.value)} placeholder='e.g. "Modular sci-fi corridor wall panel, brushed gunmetal with orange accents, game-ready low-poly style, single object on plain dark background, orthographic view"' />
                <p className="font-mono text-[9px] text-zinc-600 mt-1.5">Write the exact prompt you'd feed the Forge to produce one game-ready asset concept.</p>
              </div>
              <button data-testid="creator-apply-submit" disabled={busy} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-bold uppercase tracking-[0.2em] text-xs px-6 py-3.5 transition-colors duration-300 flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit Application — Free"}
              </button>
              <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em] text-center">
                Applying is free · approved instantly · earn-back guarantee on your first pack
              </p>
              <p className="font-mono text-[9px] text-zinc-700 tracking-[0.15em] text-center">
                Covered by our <Link to="/refunds" className="text-zinc-500 hover:text-nc-blue underline underline-offset-2 transition-colors">Refund Policy</Link> · <Link to="/privacy" className="text-zinc-500 hover:text-nc-blue underline underline-offset-2 transition-colors">Privacy Policy</Link> · Australian Consumer Law rights apply
              </p>
            </form>
          )}
        </div>
      </main>

      <SiteFooter />
      <CreatorSupportChat />
    </div>
  );
}
